import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  DialogContent,
  Grid,
  Paper,
  Chip,
  Stack,
  Typography,
  Collapse,
  Badge,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import {
  Search as SearchIcon,
  CalendarMonth as CalendarMonthIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { format, getYear } from "date-fns";
import { es } from "date-fns/locale";
import { startOfMonth, endOfMonth } from "date-fns";
import AppDialog from "../../shared/components/AppDialog";

export interface DateRange {
  startDate: Date;
  endDate: Date;
  key: string;
}

export interface ReservationQuickFilters {
  origen: string;
  saldo: string;
  departamento: string;
  movimiento: string;
  orden: string;
}

export interface ReservationSummary {
  con_saldo: number;
  saldadas: number;
  a_favor: number;
  entradas_hoy: number;
  salidas_hoy: number;
  pendiente_total: number;
}

export interface ReservationFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  dateRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
  onClearFilters: () => void;
  quickFilters?: ReservationQuickFilters;
  onQuickFiltersChange?: (filters: ReservationQuickFilters) => void;
  summary?: ReservationSummary;
  showDateFilter?: boolean;
  showSearchFilter?: boolean;
  showClearButton?: boolean;
}

const ReservationFilters: React.FC<ReservationFiltersProps> = ({
  searchTerm,
  onSearchChange,
  onSearch,
  dateRange,
  onDateRangeChange,
  onClearFilters,
  quickFilters = {
    origen: "",
    saldo: "",
    departamento: "",
    movimiento: "",
    orden: "entrada_desc",
  },
  onQuickFiltersChange,
  summary,
  showDateFilter = true,
  showSearchFilter = true,
  showClearButton = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [monthSelectorOpen, setMonthSelectorOpen] = useState(false);

  const activeExtraFiltersCount = [
    quickFilters.origen,
    quickFilters.saldo,
    quickFilters.movimiento,
    quickFilters.departamento,
    quickFilters.orden !== "entrada_desc" ? quickFilters.orden : "",
  ].filter(Boolean).length;
  const [departmentDraft, setDepartmentDraft] = useState(
    quickFilters.departamento
  );

  const selectedYear = getYear(dateRange.startDate);

  useEffect(() => {
    setDepartmentDraft(quickFilters.departamento);
  }, [quickFilters.departamento]);

  const handleMonthSelectorClick = () => {
    setMonthSelectorOpen(true);
  };

  const handleMonthSelectorClose = () => {
    setMonthSelectorOpen(false);
  };

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    const newYear = Number(event.target.value);
    const currentMonth = dateRange.startDate.getMonth();
    const newDate = new Date(newYear, currentMonth, 1);
    const newDateRange = {
      startDate: startOfMonth(newDate),
      endDate: endOfMonth(newDate),
      key: "selection",
    };
    onDateRangeChange(newDateRange);
  };

  const handleSelectMonth = (month: number) => {
    const newDate = new Date(selectedYear, month, 1);
    const newDateRange = {
      startDate: startOfMonth(newDate),
      endDate: endOfMonth(newDate),
      key: "selection",
    };

    onDateRangeChange(newDateRange);
    setMonthSelectorOpen(false);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      onSearch();
    }
  };

  const handleQuickFilterChange =
    (field: keyof ReservationQuickFilters) =>
    (event: SelectChangeEvent<string>) => {
      onQuickFiltersChange?.({
        ...quickFilters,
        [field]: event.target.value,
      });
    };

  const applyDepartmentFilter = () => {
    const normalizedDepartment = departmentDraft.trim();
    if (normalizedDepartment === quickFilters.departamento) {
      return;
    }

    onQuickFiltersChange?.({
      ...quickFilters,
      departamento: normalizedDepartment,
    });
  };

  const applyQuickPatch = (patch: Partial<ReservationQuickFilters>) => {
    onQuickFiltersChange?.({
      ...quickFilters,
      ...patch,
    });
  };

  const formatCurrency = (value: number) =>
    `$${Number(value || 0).toLocaleString("es-AR", {
      maximumFractionDigits: 0,
    })}`;

  const handleClearFilters = () => {
    const newDateRange = {
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
      key: "selection",
    };
    onDateRangeChange(newDateRange);
    onSearchChange("");
    onClearFilters();
  };

  const monthLabel = format(dateRange.startDate, "MMMM yyyy", { locale: es });

  const currentYear = getYear(new Date());
  const startYear = 2020;
  const years = Array.from(
    { length: currentYear - startYear + 6 }, // +6 para incluir 5 años futuros
    (_, i) => startYear + i
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1, sm: 1.25 },
        mb: 1.5,
        borderRadius: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      {/* Fila principal: búsqueda + mes + toggle filtros (móvil) */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {/* Filtro de búsqueda */}
        {showSearchFilter && (
          <TextField
            placeholder="Buscar por cliente, DNI/CUIT, teléfono, email o departamento..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 260 },
              maxWidth: { xs: "100%", md: 360 },
              flex: 1,
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
          />
        )}

        {/* Fila secundaria en móvil: Buscar + Mes + Toggle filtros */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            width: { xs: "100%", sm: "auto" },
            flexShrink: 0,
          }}
        >
          {/* Botón de búsqueda */}
          {showSearchFilter && (
            <Button
              variant="contained"
              onClick={onSearch}
              size="small"
              sx={{ px: 2, flexShrink: 0 }}
            >
              Buscar
            </Button>
          )}

          {/* Selector de mes */}
          {showDateFilter && (
            <Button
              variant="outlined"
              onClick={handleMonthSelectorClick}
              startIcon={<CalendarMonthIcon />}
              size="small"
              sx={{
                fontSize: isMobile ? "0.75rem" : "inherit",
                textTransform: "none",
                flexShrink: 0,
                flex: { xs: 1, sm: "none" },
                minWidth: { sm: 180 },
                justifyContent: "flex-start",
              }}
            >
              {monthLabel}
            </Button>
          )}

          {/* Botón toggle filtros extra (solo móvil) */}
          {isMobile && (
            <Badge
              badgeContent={activeExtraFiltersCount}
              color="primary"
              sx={{ flexShrink: 0 }}
            >
              <Button
                variant={filtersExpanded ? "contained" : "outlined"}
                onClick={() => setFiltersExpanded((v) => !v)}
                size="small"
                startIcon={<FilterListIcon />}
                endIcon={filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                Filtros
              </Button>
            </Badge>
          )}
        </Box>

        {/* Filtros extra: siempre visibles en desktop, colapsables en móvil */}
        <Collapse
          in={!isMobile || filtersExpanded}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
              width: "100%",
              pt: { xs: filtersExpanded ? 1 : 0, sm: 0 },
            }}
          >
            <FormControl
              size="small"
              sx={{ minWidth: { xs: "calc(50% - 4px)", sm: 140 }, flexShrink: 0, flex: { xs: 1, sm: "none" } }}
            >
              <InputLabel>Origen</InputLabel>
              <Select
                value={quickFilters.origen}
                label="Origen"
                onChange={handleQuickFilterChange("origen")}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="directo">Directo</MenuItem>
                <MenuItem value="booking">Booking</MenuItem>
                <MenuItem value="airbnb">Airbnb</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{ minWidth: { xs: "calc(50% - 4px)", sm: 140 }, flexShrink: 0, flex: { xs: 1, sm: "none" } }}
            >
              <InputLabel>Dinero</InputLabel>
              <Select
                value={quickFilters.saldo}
                label="Dinero"
                onChange={handleQuickFilterChange("saldo")}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="con_saldo">Con saldo</MenuItem>
                <MenuItem value="saldadas">Saldadas</MenuItem>
                <MenuItem value="a_favor">A favor</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{ minWidth: { xs: "calc(50% - 4px)", sm: 150 }, flexShrink: 0, flex: { xs: 1, sm: "none" } }}
            >
              <InputLabel>Movimiento</InputLabel>
              <Select
                value={quickFilters.movimiento}
                label="Movimiento"
                onChange={handleQuickFilterChange("movimiento")}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="entradas_hoy">Entradas hoy</MenuItem>
                <MenuItem value="salidas_hoy">Salidas hoy</MenuItem>
                <MenuItem value="en_curso">En curso</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Depto"
              value={departmentDraft}
              onChange={(event) => setDepartmentDraft(event.target.value)}
              onBlur={applyDepartmentFilter}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  applyDepartmentFilter();
                }
              }}
              size="small"
              sx={{ width: { xs: "calc(50% - 4px)", sm: 90 }, flexShrink: 0 }}
              inputProps={{ inputMode: "text" }}
            />

            <FormControl
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 170 }, flexShrink: 0 }}
            >
              <InputLabel>Orden</InputLabel>
              <Select
                value={quickFilters.orden || "entrada_desc"}
                label="Orden"
                onChange={handleQuickFilterChange("orden")}
              >
                <MenuItem value="entrada_desc">Más recientes</MenuItem>
                <MenuItem value="proximas">Más próximas</MenuItem>
                <MenuItem value="creadas_desc">Últimas creadas</MenuItem>
                <MenuItem value="saldo_desc">Mayor saldo</MenuItem>
                <MenuItem value="departamento_asc">Por depto</MenuItem>
              </Select>
            </FormControl>

            {/* Botón para limpiar filtro */}
            {showClearButton && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
                size="small"
                sx={{
                  fontSize: isMobile ? "0.75rem" : "inherit",
                  textTransform: "none",
                  flexShrink: 0,
                  minWidth: { xs: "100%", sm: "auto" },
                }}
              >
                Limpiar filtro
              </Button>
            )}
          </Box>
        </Collapse>
      </Box>

      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{ mt: 1 }}
      >
        <Chip
          label="Entradas hoy"
          clickable
          color={quickFilters.movimiento === "entradas_hoy" ? "primary" : "default"}
          variant={quickFilters.movimiento === "entradas_hoy" ? "filled" : "outlined"}
          onClick={() =>
            applyQuickPatch({
              movimiento:
                quickFilters.movimiento === "entradas_hoy" ? "" : "entradas_hoy",
            })
          }
        />
        <Chip
          label="Salidas hoy"
          clickable
          color={quickFilters.movimiento === "salidas_hoy" ? "primary" : "default"}
          variant={quickFilters.movimiento === "salidas_hoy" ? "filled" : "outlined"}
          onClick={() =>
            applyQuickPatch({
              movimiento:
                quickFilters.movimiento === "salidas_hoy" ? "" : "salidas_hoy",
            })
          }
        />
        <Chip
          label="Con saldo"
          clickable
          color={quickFilters.saldo === "con_saldo" ? "warning" : "default"}
          variant={quickFilters.saldo === "con_saldo" ? "filled" : "outlined"}
          onClick={() =>
            applyQuickPatch({
              saldo: quickFilters.saldo === "con_saldo" ? "" : "con_saldo",
            })
          }
        />
        {summary && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: 1 } }}>
            {summary.con_saldo} con saldo · {summary.entradas_hoy} entradas hoy ·{" "}
            {summary.salidas_hoy} salidas hoy · pendiente{" "}
            {formatCurrency(summary.pendiente_total)}
          </Typography>
        )}
      </Stack>

      {/* Modal selector de mes y año */}
      <AppDialog
        open={monthSelectorOpen}
        onClose={handleMonthSelectorClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[3],
            mt: 8,
          },
        }}
      >
        <DialogContent sx={{ p: 3, minHeight: 0, overflowY: "auto", flex: 1 }}>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Año</InputLabel>
              <Select
                value={selectedYear}
                onChange={handleYearChange}
                label="Año"
                sx={{ mb: 2 }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      mt: 1,
                    },
                  },
                }}
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Grid container spacing={1}>
            {Array.from({ length: 12 }).map((_, i) => (
              <Grid item xs={4} key={i}>
                <Button
                  fullWidth
                  onClick={() => handleSelectMonth(i)}
                  variant={
                    dateRange.startDate.getMonth() === i &&
                    dateRange.startDate.getFullYear() === selectedYear
                      ? "contained"
                      : "outlined"
                  }
                  sx={{
                    textTransform: "capitalize",
                    py: 1,
                    borderRadius: 2,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: theme.shadows[2],
                    },
                  }}
                >
                  {format(new Date(0, i), "MMMM", { locale: es })}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </AppDialog>
    </Paper>
  );
};

export default ReservationFilters;
